import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ResourceTag } from 'libs/shared/model/src/lib/widgets/resource-tag.model';
import { map, Observable } from 'rxjs';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { OrganisationConnection } from '../model/cluster.model';

export type CreateInvitationLinkResponse = {
  url: string;
  expiresAt: string;
};

export type VerifyInvitationHashResponse = {
  isValid: boolean;
  organisationId: string;
  organisationDisplayName: string;
};

export type AcceptInvitationLinkResponse = {
  organisationConnection: OrganisationConnection;
};

type ResourceTagDto = {
  id: string;
  label: string;
  color: string;
  backgroundColor: string;
};

@Injectable({
  providedIn: 'root',
})
export class OrganisationHttpService {
  private http = inject(HttpClient);
  private readonly organisationsBaseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/organisations`;

  public createInvitationLink(): Observable<CreateInvitationLinkResponse> {
    return this.http.post<CreateInvitationLinkResponse>(`${this.organisationsBaseUrl}/invitation-link`, {});
  }

  public verifyInvitationHashAsync(hash: string): Observable<VerifyInvitationHashResponse> {
    const params = new HttpParams().set('hash', hash);
    return this.http.get<VerifyInvitationHashResponse>(`${this.organisationsBaseUrl}/invitation-link/verify`, { params });
  }

  public acceptInvitationLink(hash: string): Observable<AcceptInvitationLinkResponse> {
    const params = new HttpParams().set('hash', hash);
    return this.http.put<AcceptInvitationLinkResponse>(`${this.organisationsBaseUrl}/invitation-link/accept`, {}, { params });
  }

  public getConnectedOrganisations(): Observable<OrganisationConnection[]> {
    return this.http.get<OrganisationConnection[]>(`${this.organisationsBaseUrl}/connections`);
  }

  public deleteConnection(connectionId: string): Observable<void> {
    const params = new HttpParams().set('connectionId', connectionId);
    return this.http.delete<void>(`${this.organisationsBaseUrl}/connection`, { params });
  }

  public getOrganisationResourceTags(): Observable<ResourceTag[]> {
    return this.http.get<ResourceTagDto[]>(`${this.organisationsBaseUrl}/resource-tags`).pipe(
      map((dtos) =>
        dtos.map((dto) => ({
          id: dto.id,
          label: dto.label,
          color: dto.color,
          backgroundColor: dto.backgroundColor,
        })),
      ),
    );
  }

  public addOrganisationResourceTag(resourceTag: Omit<ResourceTag, 'id'>): Observable<ResourceTag> {
    return this.http
      .post<ResourceTagDto>(`${this.organisationsBaseUrl}/resource-tags`, resourceTag)
      .pipe(map((dto) => ({ id: dto.id, label: dto.label, color: dto.color, backgroundColor: dto.backgroundColor })));
  }

  public updateOrganisationResourceTag(resourceTagId: string, resourceTag: ResourceTag): Observable<ResourceTag> {
    return this.http
      .put<ResourceTagDto>(`${this.organisationsBaseUrl}/resource-tags/${encodeURIComponent(resourceTagId)}`, resourceTag)
      .pipe(map((dto) => ({ id: dto.id, label: dto.label, color: dto.color, backgroundColor: dto.backgroundColor })));
  }

  public removeOrganisationResourceTag(resourceTagId: string): Observable<void> {
    return this.http.delete<void>(`${this.organisationsBaseUrl}/resource-tags/${encodeURIComponent(resourceTagId)}`);
  }
}
